<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div v-if="userData.permissions['view_balances'] == 1">

      <!-- Section switcher + summary toggle -->
      <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div class="btn-group section-switcher" role="group" aria-label="Balance sections">
          <button
            v-for="s in sections"
            :key="s.key"
            type="button"
            class="btn d-flex align-items-center"
            :class="activeSection === s.key ? 'btn-' + s.variant : 'btn-light'"
            @click="setSection(s.key)"
          >
            <i class="mdi fs-5 me-2" :class="s.icon"></i>
            <span class="fw-medium">{{ s.label }}</span>
            <span
              class="badge ms-2"
              :class="activeSection === s.key ? 'bg-white text-' + s.variant : 'bg-soft-' + s.variant + ' text-' + s.variant"
            >{{ s.count }}</span>
          </button>
        </div>

        <b-button variant="soft-secondary" size="sm" @click="view_summary = !view_summary">
          <i class="mdi" :class="view_summary ? 'mdi-eye-off' : 'mdi-eye'"></i>
          {{ view_summary ? 'Hide Summary' : 'Show Summary' }}
        </b-button>
      </div>

      <transition name="section-swap" mode="out-in">
        <div :key="activeSection">

          <!-- Section summary cards (only for the active section) -->
          <b-collapse v-model="view_summary">
            <div class="row mb-3">
              <div class="col-sm-6 col-xl-3 mb-3 mb-xl-0" v-for="card in sectionCards" :key="card.label">
                <div class="card mini-stats-wid shadow-sm border-0 h-100 mb-0">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium mb-2">{{ card.label }}</p>
                        <h4 class="mb-1" :class="'text-' + card.variant">
                          <span v-if="card.money" class="fs-6 fw-medium">{{ currency }}</span>
                          {{ card.value }}
                        </h4>
                        <small class="text-muted">{{ card.sub }}</small>
                      </div>
                      <div class="mini-stat-icon avatar-sm rounded-circle align-self-center" :class="'bg-soft-' + card.variant">
                        <span class="avatar-title text-white fs-3"><i class="mdi" :class="card.icon"></i></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-collapse>

          <!-- Table card -->
          <div class="card shadow-sm border-0">
            <div class="card-body">
              <div class="row align-items-center mb-3 g-2">
                <div class="col-md-4">
                  <label class="d-inline-flex align-items-center mb-0">
                    Show
                    <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm" :options="Options"></b-form-select>
                    entries
                  </label>
                </div>
                <div class="col-md-4 text-md-center">
                  <div class="form-check form-switch d-inline-block mb-0">
                    <input class="form-check-input" type="checkbox" id="hideEmpty" v-model="hideEmpty" />
                    <label class="form-check-label" for="hideEmpty">{{ currentSection.emptyLabel }}</label>
                  </div>
                </div>
                <div class="col-md-4 text-md-end">
                  <b-form-input v-model="filter" type="search" placeholder="Search by name or member no..." class="form-control form-control-sm d-inline-block w-auto"></b-form-input>
                </div>
              </div>

              <b-table
                :items="filteredItems"
                :fields="Fields"
                responsive="sm"
                show-empty
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :busy="IsBusy"
                class="align-middle table-hover custom-ledger-table mb-0"
              >
                <template #table-busy>
                  <div class="text-center text-primary my-5">
                    <b-spinner class="align-middle" type="grow"></b-spinner>
                    <div class="mt-2 fw-medium">Loading Account Balances...</div>
                  </div>
                </template>

                <template #empty>
                  <div class="text-center py-5">
                    <i class="mdi mdi-account-search text-muted" style="font-size: 64px;"></i>
                    <h5 class="text-dark mt-2">No members to show</h5>
                    <p class="text-muted mb-0">
                      Try a different search, or turn off “{{ currentSection.emptyLabel }}” to see every member.
                    </p>
                  </div>
                </template>

                <!-- Member -->
                <template #cell(member_name)="data">
                  <router-link :to="{ path: '/member-profile', query: { id: data.item.member.member_id } }" class="text-decoration-none text-dark">
                    <div class="d-flex align-items-center">
                      <div class="avatar-xs me-3 flex-shrink-0">
                        <span class="avatar-title rounded-circle bg-soft-primary text-white fw-bold">
                          {{ data.item.member.name.charAt(0) }}
                        </span>
                      </div>
                      <div>
                        <h6 class="mb-0 text-dark fw-bold member-name-hover">{{ data.item.member.name }}</h6>
                        <small class="text-muted">{{ data.item.member.member_id }}</small>
                      </div>
                    </div>
                  </router-link>
                </template>

                <!-- Savings -->
                <template #cell(current_savings)="data">
                  <div v-if="data.item.current_savings > 0" class="metric-cell ms-auto">
                    <span class="text-success fw-bold">{{ commas(data.item.current_savings) }}</span>
                    <div class="progress progress-thin">
                      <div class="progress-bar bg-success" :style="{ width: pct(data.item.current_savings, max.current_savings) + '%' }"></div>
                    </div>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(total_savings)="data">
                  <span v-if="data.item.total_savings > 0" class="text-dark fw-medium">{{ commas(data.item.total_savings) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(withdraws)="data">
                  <span v-if="data.item.withdraws > 0" class="text-secondary">{{ commas(data.item.withdraws) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>

                <!-- Loans -->
                <template #cell(running_loans)="data">
                  <div v-if="data.item.running_loans > 0" class="metric-cell ms-auto">
                    <span class="text-danger fw-bold">{{ commas(data.item.running_loans) }}</span>
                    <div class="progress progress-thin">
                      <div class="progress-bar bg-danger" :style="{ width: pct(data.item.running_loans, max.running_loans) + '%' }"></div>
                    </div>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(total_interest)="data">
                  <span v-if="data.item.total_interest > 0" class="text-warning fw-medium">{{ commas(data.item.total_interest) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(total_fines)="data">
                  <span v-if="data.item.total_fines > 0" class="badge bg-soft-danger text-danger p-1 fs-6">{{ commas(data.item.total_fines) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(total_loan)="data">
                  <span v-if="data.item.total_loan > 0" class="text-dark fw-medium">{{ commas(data.item.total_loan) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>

                <!-- Shares -->
                <template #cell(shares_count)="data">
                  <div v-if="data.item.shares_count > 0" class="metric-cell ms-auto">
                    <span class="text-info fw-bold">{{ commas(data.item.shares_count) }} <small class="text-muted fw-normal">shares</small></span>
                    <div class="progress progress-thin">
                      <div class="progress-bar bg-info" :style="{ width: pct(data.item.shares_count, max.shares_count) + '%' }"></div>
                    </div>
                  </div>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(shares_amount)="data">
                  <span v-if="data.item.shares_amount > 0" class="text-dark fw-medium">{{ commas(data.item.shares_amount) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>
                <template #cell(ownership)="data">
                  <span v-if="data.item.ownership > 0" class="badge bg-soft-info text-info p-1 fs-6">{{ data.item.ownership.toFixed(1) }}%</span>
                  <span v-else class="text-muted">-</span>
                </template>

                <!-- Expand: full member snapshot across all sections -->
                <template #cell(details)="data">
                  <b-button size="sm" variant="soft-secondary" class="px-2" @click="data.toggleDetails" :title="data.detailsShowing ? 'Hide snapshot' : 'Show snapshot'">
                    <i class="mdi fs-5" :class="data.detailsShowing ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i>
                  </b-button>
                </template>

                <template #row-details="data">
                  <div class="member-snapshot rounded p-3 mx-1 mb-1">
                    <div class="row g-3 align-items-center">
                      <div class="col-md-3 d-flex align-items-center">
                        <i class="mdi mdi-cash-multiple text-success fs-2 me-3"></i>
                        <div>
                          <small class="text-muted d-block">Net savings</small>
                          <span class="fw-bold">{{ currency }} {{ commas(data.item.current_savings) || 0 }}</span>
                          <small class="text-muted d-block">Withdrawn {{ commas(data.item.withdraws) || 0 }}</small>
                        </div>
                      </div>
                      <div class="col-md-3 d-flex align-items-center">
                        <i class="mdi mdi-bank text-danger fs-2 me-3"></i>
                        <div>
                          <small class="text-muted d-block">Running loan</small>
                          <span class="fw-bold">{{ currency }} {{ commas(data.item.running_loans) || 0 }}</span>
                          <small class="text-muted d-block">
                            Interest {{ commas(data.item.total_interest) || 0 }} · Fines {{ commas(data.item.total_fines) || 0 }}
                          </small>
                        </div>
                      </div>
                      <div class="col-md-3 d-flex align-items-center">
                        <i class="mdi mdi-chart-line text-info fs-2 me-3"></i>
                        <div>
                          <small class="text-muted d-block">Shares</small>
                          <span class="fw-bold">{{ commas(data.item.shares_count) || 0 }}</span>
                          <small class="text-muted d-block">Worth {{ currency }} {{ commas(data.item.shares_amount) || 0 }}</small>
                        </div>
                      </div>
                      <div class="col-md-3 text-md-end">
                        <router-link :to="{ path: '/member-profile', query: { id: data.item.member.member_id } }" class="btn btn-sm btn-primary">
                          <i class="mdi mdi-account-outline me-1"></i> Open profile
                        </router-link>
                      </div>
                    </div>
                  </div>
                </template>
              </b-table>

              <div class="d-flex flex-wrap justify-content-between align-items-center mt-3" v-if="List > 0">
                <small class="text-muted">Showing {{ showingFrom }}–{{ showingTo }} of {{ List }} members</small>
                <b-pagination v-model="currentPage" :total-rows="List" :per-page="perPage" class="mb-0"></b-pagination>
              </div>
            </div>
          </div>

        </div>
      </transition>
    </div>

    <div v-else class="row justify-content-center mt-5">
      <div class="col-md-8 col-xl-6 text-center">
        <div class="py-5 bg-white rounded shadow-sm">
          <i class="mdi mdi-lock-outline text-muted" style="font-size: 72px;"></i>
          <h4 class="text-dark mt-3">Access Denied</h4>
          <p class="text-muted mb-0">You do not have the required permissions to view account balances.</p>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { accountbalanceMethods, accountbalanceComputed } from "@/state/helpers";

export default {
  page: {
    title: "Account Balances",
    meta: [{ name: "description", content: appConfig.description, },],
  },
  components: {
    Layout, PageHeader
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")) || { currency: 'UGX' },
      title: "Account Balances",
      items: [{ text: "Dashboard", to: "/", }, { text: "Account Balances", active: true, },],
      currentPage: 1,
      perPage: 10,
      Options: [10, 25, 50, 100],
      filter: "",
      view_summary: true,
      loaded: false,

      // Only one section is shown at a time
      activeSection: 'savings',
      hideEmpty: true,
      sortBy: 'current_savings',
      sortDesc: true,
    };
  },
  computed: {
    ...accountbalanceComputed,

    currency() { return this.userData.currency || 'UGX'; },
    IsBusy() { return !this.loaded && this.accountbalances.length === 0; },

    // Normalised rows: numbers coerced, shares flattened so every column is sortable
    Items() {
      const totalShares = this.accountbalances.reduce((a, b) => a + (Number(b.total_shares && b.total_shares.shares) || 0), 0);
      return this.accountbalances.map((d) => {
        const sharesCount = Number(d.total_shares && d.total_shares.shares) || 0;
        return {
          ...d,
          member_name: d.member.name,
          member_no: d.member.member_id,
          current_savings: Number(d.current_savings) || 0,
          total_savings: Number(d.total_savings) || 0,
          withdraws: Number(d.withdraws) || 0,
          running_loans: Number(d.running_loans) || 0,
          total_interest: Math.round(Number(d.total_interest) || 0),
          total_fines: Math.round(Number(d.total_fines) || 0),
          total_loan: Number(d.total_loan) || 0,
          shares_count: sharesCount,
          shares_amount: Number(d.total_shares && d.total_shares.amount) || 0,
          ownership: totalShares > 0 ? (sharesCount / totalShares) * 100 : 0,
          _showDetails: false,
        };
      });
    },

    // ---- Section definitions ----
    sections() {
      const I = this.Items;
      return [
        {
          key: 'savings', label: 'Savings', icon: 'mdi-cash-multiple', variant: 'success',
          count: I.filter(this.hasSavings).length,
          emptyLabel: 'Only members with savings',
        },
        {
          key: 'loans', label: 'Loans', icon: 'mdi-bank', variant: 'danger',
          count: I.filter(this.hasLoans).length,
          emptyLabel: 'Only members with loans',
        },
        {
          key: 'shares', label: 'Shares', icon: 'mdi-chart-line', variant: 'info',
          count: I.filter(this.hasShares).length,
          emptyLabel: 'Only shareholders',
        },
      ];
    },
    currentSection() { return this.sections.find(s => s.key === this.activeSection); },

    // ---- Totals ----
    totals() {
      const I = this.Items;
      const sum = (k) => I.reduce((a, b) => a + b[k], 0);
      return {
        currentSavings: sum('current_savings'),
        totalSavings: sum('total_savings'),
        withdraws: sum('withdraws'),
        runningLoans: sum('running_loans'),
        interest: sum('total_interest'),
        fines: sum('total_fines'),
        totalLoan: sum('total_loan'),
        shares: sum('shares_count'),
        sharesValue: sum('shares_amount'),
      };
    },

    max() {
      const m = (k) => Math.max(0, ...this.Items.map(i => i[k]));
      return { current_savings: m('current_savings'), running_loans: m('running_loans'), shares_count: m('shares_count') };
    },

    // ---- Summary cards for the active section ----
    sectionCards() {
      const t = this.totals;
      const I = this.Items;
      if (this.activeSection === 'savings') {
        const savers = I.filter(i => i.current_savings > 0).length;
        return [
          { label: 'Net Current Savings', value: this.commas(t.currentSavings) || 0, money: true, variant: 'success', icon: 'mdi-cash-multiple', sub: 'After withdrawals & charges' },
          { label: 'Total Deposited', value: this.commas(t.totalSavings) || 0, money: true, variant: 'primary', icon: 'mdi-cash-plus', sub: 'All approved savings' },
          { label: 'Total Withdrawn', value: this.commas(t.withdraws) || 0, money: true, variant: 'secondary', icon: 'mdi-cash-minus',
            sub: t.totalSavings > 0 ? `${((t.withdraws / t.totalSavings) * 100).toFixed(1)}% of deposits` : 'No deposits yet' },
          { label: 'Active Savers', value: `${savers} / ${I.length}`, money: false, variant: 'info', icon: 'mdi-account-group',
            sub: savers > 0 ? `Avg balance ${this.currency} ${this.commas(Math.round(t.currentSavings / savers)) || 0}` : 'No balances yet' },
        ];
      }
      if (this.activeSection === 'loans') {
        const borrowers = I.filter(i => i.running_loans > 0).length;
        return [
          { label: 'Running Loans', value: this.commas(t.runningLoans) || 0, money: true, variant: 'danger', icon: 'mdi-bank',
            sub: `${borrowers} active ${borrowers === 1 ? 'borrower' : 'borrowers'}` },
          { label: 'Interest', value: this.commas(t.interest) || 0, money: true, variant: 'warning', icon: 'mdi-percent-outline', sub: 'Accrued on loans' },
          { label: 'Fines', value: this.commas(t.fines) || 0, money: true, variant: 'danger', icon: 'mdi-alert-circle-outline', sub: 'Late & missed instalments' },
          { label: 'Total Disbursed', value: this.commas(t.totalLoan) || 0, money: true, variant: 'secondary', icon: 'mdi-history', sub: 'Running + closed loans' },
        ];
      }
      const holders = I.filter(i => i.shares_count > 0).length;
      return [
        { label: 'Total Shares', value: this.commas(t.shares) || 0, money: false, variant: 'info', icon: 'mdi-chart-line', sub: 'Issued to members' },
        { label: 'Shares Value', value: this.commas(t.sharesValue) || 0, money: true, variant: 'primary', icon: 'mdi-cash-multiple', sub: 'Amount paid in' },
        { label: 'Shareholders', value: `${holders} / ${I.length}`, money: false, variant: 'success', icon: 'mdi-account-group', sub: 'Members holding shares' },
        { label: 'Avg Price / Share', value: this.commas(t.shares > 0 ? Math.round(t.sharesValue / t.shares) : 0) || 0, money: true, variant: 'secondary', icon: 'mdi-tag-outline', sub: 'Paid per share' },
      ];
    },

    // ---- Table columns for the active section ----
    Fields() {
      const member = { key: 'member_name', label: 'Member', sortable: true, class: 'align-middle' };
      const num = (key, label, extra = {}) => ({ key, label, sortable: true, class: 'align-middle text-end', ...extra });
      const details = { key: 'details', label: '', class: 'text-end', thStyle: { width: '60px' } };

      if (this.activeSection === 'savings') {
        return [member, num('current_savings', 'Current Balance', { thStyle: { minWidth: '180px' } }), num('total_savings', 'Total Deposited'), num('withdraws', 'Withdrawn'), details];
      }
      if (this.activeSection === 'loans') {
        return [member, num('running_loans', 'Running', { thStyle: { minWidth: '180px' } }), num('total_interest', 'Interest'), num('total_fines', 'Fines'), num('total_loan', 'Total Disbursed'), details];
      }
      return [member, num('shares_count', 'Shares Held', { thStyle: { minWidth: '180px' } }), num('shares_amount', 'Value'), num('ownership', 'Ownership'), details];
    },

    // ---- Search + "only members with…" filter ----
    filteredItems() {
      const q = (this.filter || '').toString().trim().toLowerCase();
      const rel = { savings: this.hasSavings, loans: this.hasLoans, shares: this.hasShares }[this.activeSection];
      return this.Items.filter(i => {
        if (this.hideEmpty && !rel(i)) return false;
        if (!q) return true;
        return i.member_name.toLowerCase().includes(q) || String(i.member_no).toLowerCase().includes(q);
      });
    },
    List() { return this.filteredItems.length; },
    showingFrom() { return this.List === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1; },
    showingTo() { return Math.min(this.currentPage * this.perPage, this.List); },
  },
  watch: {
    filter() { this.currentPage = 1; },
    perPage() { this.currentPage = 1; },
    hideEmpty() { this.currentPage = 1; },
  },
  mounted() {
    Promise.resolve(this.fetchAccountBalances()).finally(() => { this.loaded = true; });
  },
  methods: {
    ...accountbalanceMethods,

    setSection(key) {
      if (this.activeSection === key) return;
      this.activeSection = key;
      this.currentPage = 1;
      this.sortDesc = true;
      this.sortBy = { savings: 'current_savings', loans: 'running_loans', shares: 'shares_count' }[key];
    },

    hasSavings(i) { return i.total_savings > 0 || i.withdraws > 0; },
    hasLoans(i) { return i.total_loan > 0 || i.running_loans > 0; },
    hasShares(i) { return i.shares_count > 0; },

    pct(value, maxValue) {
      return maxValue > 0 ? Math.min(100, Math.round((value / maxValue) * 100)) : 0;
    },
    commas(x) {
      const n = Math.round(Number(x) || 0);
      return n ? n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0;
    },
  },
};
</script>

<style scoped>
/* Section switcher */
.section-switcher .btn {
  padding: 0.55rem 1.1rem;
}

/* Swap animation: only responds to the person changing sections */
.section-swap-enter-active,
.section-swap-leave-active {
  transition: opacity 0.18s ease;
}
.section-swap-enter,
.section-swap-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .section-swap-enter-active,
  .section-swap-leave-active {
    transition: none;
  }
}

/* Ledger table */
.custom-ledger-table :deep(th) {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.custom-ledger-table :deep(td) {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

/* Value + thin bar showing each member relative to the largest in the group */
.metric-cell {
  min-width: 150px;
  max-width: 220px;
}
.progress-thin {
  height: 4px;
  margin-top: 4px;
}

/* Expanded member snapshot */
.member-snapshot {
  background-color: #f8f9fa;
  border: 1px solid #eff2f7;
}

.member-name-hover {
  transition: color 0.2s ease-in-out;
}

.router-link-active:hover .member-name-hover,
a:hover .member-name-hover {
  color: var(--bs-primary) !important;
  text-decoration: underline;
}
</style>